import { Invitation, UserRole } from '@avanzu/types';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import {
  cancelInvitation,
  canCancelInvitation,
  getEffectiveStatus,
} from '../../../services/invitation';
import { resendInvitation } from '../../../services/invitation';
import { formatDate } from '../../../utils/formatDate';
import getRoleColor from '../../../helpers/getRoleColor';
import getRoleLabel from '../../../helpers/getRoleLabel';
import getStatusColor from '../../../helpers/getStatusColor';

interface TeamPendingInvitationRowProps {
  invitation: Invitation;
  organisationId: string;
  currentUserRole: UserRole;
  onInvitationUpdated: (updatedInvitation: Invitation) => void;
  onInvitationDeleted: (invitationId: string) => void;
}

const TeamPendingInvitationRow: React.FC<TeamPendingInvitationRowProps> = ({
  invitation,
  organisationId,
  currentUserRole,
  onInvitationUpdated,
  onInvitationDeleted,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Permission checks
  const canManageTeam = currentUserRole === UserRole.OWNER || currentUserRole === UserRole.ADMIN;
  const effectiveStatus = getEffectiveStatus(invitation);
  const showActions = canCancelInvitation(invitation);

  // Event handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
    setDialogError(null);
    handleMenuClose();
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setDialogError(null);
  };

  const handleCopyToken = async () => {
    try {
      const inviteUrl = `${window.location.origin}/join/${invitation.token}`;
      await navigator.clipboard.writeText(inviteUrl);
      enqueueSnackbar('Invitation link copied to clipboard', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to copy invitation link', { variant: 'error' });
    }
  };

  const handleResendInvitation = async () => {
    try {
      setIsSubmitting(true);
      setDialogError(null);

      const result = await resendInvitation(invitation.token);

      // Update the invitation if expiry was extended
      if (result.wasExtended) {
        const updatedInvitation = {
          ...invitation,
          expiresAt: new Date(result.invitation.expiresAt),
        };
        onInvitationUpdated(updatedInvitation);
      }

      enqueueSnackbar(
        `Invitation resent successfully to ${invitation.email}${
          result.wasExtended ? ' (expiry extended)' : ''
        }`,
        { variant: 'success' }
      );

      handleMenuClose();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to resend invitation', { variant: 'error' });
      handleMenuClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelInvitation = async () => {
    try {
      setIsSubmitting(true);
      setDialogError(null);

      await cancelInvitation(invitation.id, organisationId);

      onInvitationDeleted(invitation.id);
      enqueueSnackbar('Invitation cancelled successfully', { variant: 'success' });
      handleCloseCancelDialog();
    } catch (err: any) {
      setDialogError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatToken = (token?: string) => {
    if (!token) return 'invalid token';
    if (token.length <= 12) return token;
    return `${token.substring(0, 8)}...${token.substring(token.length - 4)}`;
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'grey.100', color: 'grey.300' }}>
              <EmailIcon />
            </Avatar>
            <Box>
              <Typography variant="body1">{invitation.email}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    backgroundColor: 'grey.100',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                  }}
                >
                  {formatToken(invitation.token)}
                </Typography>
                <Tooltip title="Copy Invitation Link">
                  <IconButton size="small" onClick={handleCopyToken} sx={{ p: 0.5 }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip label={getRoleLabel(invitation.role)} size="small" color={getRoleColor(invitation.role)} />
        </TableCell>
        <TableCell>
          <Chip
            label={effectiveStatus}
            size="small"
            sx={{ backgroundColor: getStatusColor(effectiveStatus) }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(invitation.acceptedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            Expires: {formatDate(invitation.expiresAt)}
          </Typography>
        </TableCell>
        {canManageTeam && (
          <TableCell align="right">
            {showActions && (
              <IconButton size="small" onClick={handleMenuOpen} disabled={isSubmitting}>
                <MoreVertIcon />
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleCopyToken} disabled={isSubmitting}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy invitation link
        </MenuItem>
        <MenuItem onClick={handleResendInvitation} disabled={isSubmitting}>
          <SendIcon fontSize="small" sx={{ mr: 1 }} />
          Resend Invitation
        </MenuItem>
        <MenuItem onClick={handleOpenCancelDialog} sx={{ color: 'error.main' }}>
          <CancelIcon fontSize="small" sx={{ mr: 1 }} />
          Cancel Invitation
        </MenuItem>
      </Menu>

      {/* Cancel Invitation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog}>
        <DialogTitle>Cancel Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the invitation for {invitation.email}? This action
            cannot be undone and the recipient will no longer be able to accept the invitation.
          </DialogContentText>

          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={isSubmitting}>
            Keep Invitation
          </Button>
          <Button
            onClick={handleCancelInvitation}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Cancelling...' : 'Cancel Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamPendingInvitationRow;
